import { Box, Table, Sheet, Switch, Tooltip, Option, Textarea } from "@mui/joy";
import { SaveIcon, DeleteIcon, EditIcon, CancelIcon } from "../../../styles/StyledMUI";
import DarkTypography from "../../common/DarkTypography";
import DarkInput from "../../common/DarkInput";
import DarkSelect from "../../common/DarkSelect";
import IconButtonCTA from "../../common/IconButtonCTA";
import type { LeaveType } from "../../../api/leave/Leave.typed";
import type { NamedColor } from "../../../utils/colorPalette";
import { isPlatformLeaveType } from "../../../utils/leaveHelpers";
import { colorTokens, hrFilterSelectSx, hrSheetSx, hrTableSx } from "../../../styles/globalStyles";

export type LeaveTypesTableProps = {
  types: LeaveType[];
  colorSet: NamedColor[];
  loading: boolean;
  editingId: number | null;
  draft: Partial<LeaveType>;
  onDraftChange: (draft: Partial<LeaveType>) => void;
  onStartEdit: (lt?: LeaveType) => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onRemove: (id: number) => void;
  canEdit?: boolean;
};

export default function LeaveTypesTable({
  types,
  colorSet,
  loading,
  editingId,
  draft,
  onDraftChange,
  onStartEdit,
  onCancelEdit,
  onSave,
  onRemove,
  canEdit = true,
}: LeaveTypesTableProps) {
  return (
    <Sheet sx={hrSheetSx}>
      <Table hoverRow sx={hrTableSx}>
        <thead>
          <tr>
            <th>Nom</th>
            <th>Actif</th>
            <th>Validation requise</th>
            <th>Compteur</th>
            <th>Unité</th>
            <th>Acc. mensuelle</th>
            <th>Négatif autorisé</th>
            <th>Couleur</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {canEdit && editingId === 0 && (
            <tr>
              <td>
                <DarkInput
                  value={draft.Name ?? ""}
                  onChange={(e) => onDraftChange({ ...draft, Name: e.target.value })}
                  size="sm"
                />
              </td>
              <td>
                <Switch
                  checked={!!draft.IsActive}
                  onChange={(e) => onDraftChange({ ...draft, IsActive: e.target.checked })}
                />
              </td>
              <td>
                <Switch
                  checked={!!draft.RequiresValidation}
                  onChange={(e) =>
                    onDraftChange({ ...draft, RequiresValidation: e.target.checked })
                  }
                />
              </td>
              <td>
                <Switch
                  checked={!!draft.UsesCounter}
                  onChange={(e) => onDraftChange({ ...draft, UsesCounter: e.target.checked })}
                />
              </td>
              <td>
                <DarkSelect
                  value={(draft.Unit as string) || "days"}
                  onChange={(_, v) =>
                    onDraftChange({ ...draft, Unit: (v as "days" | "hours") || "days" })
                  }
                  size="sm"
                  disabled={!draft.UsesCounter}
                >
                  <Option value="days">Jours</Option>
                  <Option value="hours">Heures</Option>
                </DarkSelect>
              </td>
              <td>
                <DarkInput
                  type="number"
                  value={draft.DefaultMonthlyAccrual as number | ""}
                  onChange={(e) =>
                    onDraftChange({
                      ...draft,
                      DefaultMonthlyAccrual:
                        e.target.value === "" ? undefined : Number(e.target.value),
                    })
                  }
                  size="sm"
                  disabled={!draft.UsesCounter}
                />
              </td>
              <td>
                <Switch
                  checked={!!draft.AllowsNegative}
                  onChange={(e) => onDraftChange({ ...draft, AllowsNegative: e.target.checked })}
                  disabled={!draft.UsesCounter}
                />
              </td>
              <td>
                <DarkSelect
                  value={(draft.Color as string) || colorSet[0]?.hex}
                  onChange={(_, v) =>
                    onDraftChange({ ...draft, Color: (v as string) || draft.Color })
                  }
                  size="sm"
                >
                  {colorSet.map(({ hex, name }) => (
                    <Option key={hex} value={hex}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 12,
                            borderRadius: 0.5,
                            backgroundColor: hex,
                            border: `1px solid ${colorTokens.border.default}`,
                          }}
                        />
                        {name} ({hex})
                      </Box>
                    </Option>
                  ))}
                </DarkSelect>
              </td>
              <td>
                <Textarea
                  minRows={2}
                  value={draft.Description ?? ""}
                  onChange={(e) => onDraftChange({ ...draft, Description: e.target.value })}
                  sx={{ ...hrFilterSelectSx }}
                />
              </td>
              <td>
                <IconButtonCTA
                  ctaVariant="success"
                  onClick={onSave}
                  sx={{ mr: 1 }}
                  disabled={loading || !String(draft.Name ?? "").trim()}
                  aria-label="Enregistrer"
                >
                  <SaveIcon />
                </IconButtonCTA>
                <IconButtonCTA
                  ctaVariant="neutral"
                  onClick={onCancelEdit}
                  disabled={loading}
                  aria-label="Annuler"
                >
                  <CancelIcon />
                </IconButtonCTA>
              </td>
            </tr>
          )}
          {types.map((t) => {
            const rowEditing = editingId === t.ID && !isPlatformLeaveType(t);
            return (
              <tr
                key={t.ID}
                onDoubleClick={() =>
                  canEdit &&
                  !loading &&
                  editingId === null &&
                  !isPlatformLeaveType(t) &&
                  onStartEdit(t)
                }
              >
                <td>
                  {rowEditing ? (
                    <DarkInput
                      value={String(draft.Name ?? "")}
                      onChange={(e) => onDraftChange({ ...draft, Name: e.target.value })}
                      size="sm"
                    />
                  ) : (
                    <DarkTypography level="body-sm">{t.Name}</DarkTypography>
                  )}
                </td>
                <td>
                  {rowEditing ? (
                    <Switch
                      checked={!!draft.IsActive}
                      onChange={(e) => onDraftChange({ ...draft, IsActive: e.target.checked })}
                    />
                  ) : t.IsActive ? (
                    "Oui"
                  ) : (
                    "Non"
                  )}
                </td>
                <td>
                  {rowEditing ? (
                    <Switch
                      checked={!!draft.RequiresValidation}
                      onChange={(e) =>
                        onDraftChange({ ...draft, RequiresValidation: e.target.checked })
                      }
                    />
                  ) : t.RequiresValidation ? (
                    "Oui"
                  ) : (
                    "Non"
                  )}
                </td>
                <td>
                  {rowEditing ? (
                    <Switch
                      checked={!!draft.UsesCounter}
                      onChange={(e) => onDraftChange({ ...draft, UsesCounter: e.target.checked })}
                    />
                  ) : t.UsesCounter ? (
                    "Oui"
                  ) : (
                    "Non"
                  )}
                </td>
                <td>
                  {rowEditing ? (
                    <DarkSelect
                      value={(draft.Unit as string) || t.Unit || "days"}
                      onChange={(_, v) =>
                        onDraftChange({ ...draft, Unit: (v as "days" | "hours") || "days" })
                      }
                      size="sm"
                      disabled={!draft.UsesCounter}
                    >
                      <Option value="days">Jours</Option>
                      <Option value="hours">Heures</Option>
                    </DarkSelect>
                  ) : t.Unit === "hours" ? (
                    "Heures"
                  ) : (
                    "Jours"
                  )}
                </td>
                <td>
                  {rowEditing ? (
                    <DarkInput
                      type="number"
                      value={draft.DefaultMonthlyAccrual as number | ""}
                      onChange={(e) =>
                        onDraftChange({
                          ...draft,
                          DefaultMonthlyAccrual:
                            e.target.value === "" ? undefined : Number(e.target.value),
                        })
                      }
                      size="sm"
                      disabled={!draft.UsesCounter}
                    />
                  ) : (t.DefaultMonthlyAccrual ?? "") === "" ? (
                    "—"
                  ) : (
                    String(t.DefaultMonthlyAccrual)
                  )}
                </td>
                <td>
                  {rowEditing ? (
                    <Switch
                      checked={!!draft.AllowsNegative}
                      onChange={(e) =>
                        onDraftChange({ ...draft, AllowsNegative: e.target.checked })
                      }
                      disabled={!draft.UsesCounter}
                    />
                  ) : t.AllowsNegative ? (
                    "Oui"
                  ) : (
                    "Non"
                  )}
                </td>
                <td>
                  {rowEditing ? (
                    <DarkSelect
                      value={(draft.Color as string) || t.Color}
                      onChange={(_, v) =>
                        onDraftChange({ ...draft, Color: (v as string) || draft.Color })
                      }
                      size="sm"
                    >
                      {colorSet.map(({ hex, name }) => (
                        <Option key={hex} value={hex}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 12,
                                borderRadius: 0.5,
                                backgroundColor: hex,
                                border: `1px solid ${colorTokens.border.default}`,
                              }}
                            />
                            {name} ({hex})
                          </Box>
                        </Option>
                      ))}
                    </DarkSelect>
                  ) : (
                    <Tooltip title={t.Color} placement="top" variant="soft">
                      <Box
                        sx={{
                          width: 24,
                          height: 16,
                          borderRadius: 0.5,
                          backgroundColor: t.Color,
                          border: `1px solid ${colorTokens.border.default}`,
                        }}
                      />
                    </Tooltip>
                  )}
                </td>
                <td>
                  {rowEditing ? (
                    <Textarea
                      minRows={2}
                      value={draft.Description ?? ""}
                      onChange={(e) => onDraftChange({ ...draft, Description: e.target.value })}
                      sx={{ ...hrFilterSelectSx }}
                    />
                  ) : t.Description && String(t.Description).trim() ? (
                    t.Description
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  {canEdit && editingId === t.ID ? (
                    isPlatformLeaveType(t) ? (
                      <IconButtonCTA
                        ctaVariant="neutral"
                        onClick={onCancelEdit}
                        disabled={loading}
                        aria-label="Annuler"
                      >
                        <CancelIcon />
                      </IconButtonCTA>
                    ) : (
                      <>
                        <IconButtonCTA
                          ctaVariant="success"
                          onClick={onSave}
                          sx={{ mr: 1 }}
                          disabled={loading || !String(draft.Name ?? "").trim()}
                          aria-label="Enregistrer"
                        >
                          <SaveIcon />
                        </IconButtonCTA>
                        <IconButtonCTA
                          ctaVariant="neutral"
                          onClick={onCancelEdit}
                          disabled={loading}
                          aria-label="Annuler"
                        >
                          <CancelIcon />
                        </IconButtonCTA>
                      </>
                    )
                  ) : canEdit ? (
                    isPlatformLeaveType(t) ? (
                      <DarkTypography level="body-xs" colorVariant="muted">
                        -
                      </DarkTypography>
                    ) : (
                      <>
                        <IconButtonCTA
                          ctaVariant="accent"
                          onClick={() => onStartEdit(t)}
                          sx={{ mr: 1 }}
                          disabled={loading}
                          aria-label="Modifier"
                        >
                          <EditIcon />
                        </IconButtonCTA>
                        <IconButtonCTA
                          ctaVariant="danger"
                          onClick={() => onRemove(t.ID)}
                          disabled={loading}
                          aria-label="Supprimer"
                        >
                          <DeleteIcon />
                        </IconButtonCTA>
                      </>
                    )
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Sheet>
  );
}
